import { useMemo } from "react"
import { Link, useParams } from "react-router-dom"
import { useTranslation } from "providers/i18n"
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  CardMedia,
  List,
  Typography,
  useTheme,
  Button,
  Divider,
} from "@mui/material"

import { useEventDetail } from "hooks/event/useEventDetail"

import { PageLayout } from "layouts/PageLayout"

import { LocationIcon } from "components/icons"
import { Head } from "components/Head"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { Carousel, CarouselItem } from "components/Carousel"

import { getIcon } from "utils/icon"
import { InfoItem } from "components/InfoItem"
import { IPageLayoutProps } from "layouts"

interface IEventDetailProps {
  Layout?: React.ComponentType<IPageLayoutProps>
  AppNavigation?: React.ComponentType
}

export const EventDetail = ({
  Layout = PageLayout,
  AppNavigation,
}: IEventDetailProps) => {
  const params = useParams()
  const { eventId } = params

  const { t } = useTranslation()

  const { event, navigateToLocation } = useEventDetail(eventId)

  const theme = useTheme()
  const styleOverrides = theme.components[`VuiDetailCard`]?.styleOverrides || {}
  const {
    card,
    infoContainer,
    infoListItem,
    infoDivider,
    contentWrapper,
    cardContent,
    websiteContainer,
    websiteTitle,
    websiteLinkText,
    cardHeader,
    viewLocationContainer,
    viewLocationButton,
    cardContentPrimaryText,
    cardContentSecondaryText,
    galleryCarouselWrapper,
    galleryCarousel,
    galleryCarouselItem,
    pageLayout,
  } = styleOverrides

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )

  const hasManyImages = useMemo(
    () => event?.allImages.length > 1,
    [event?.allImages]
  )

  if (!event) return null

  return (
    <>
      <Head title={`${t(event.name)} (${t("Event Detail")})`} />
      <Layout
        sx={pageLayout}
        AppNavigation={AppNavigation}
        className={!event.hasLocation ? "no-location" : ""}
      >
        <Card sx={card} elevation={0}>
          {event.allImages && (
            <Box sx={galleryCarouselWrapper}>
              <Carousel
                sx={galleryCarousel}
                options={{
                  type: "fade",
                  padding: 0,
                  gap: 0,
                  pagination: hasManyImages,
                  autoplay: true,
                  drag: hasManyImages,
                }}
              >
                {event.allImages.map(({ id, url }) => {
                  return (
                    <CarouselItem key={`slide-${id}`} sx={galleryCarouselItem}>
                      <CardMedia
                        sx={galleryCarouselItem}
                        component="img"
                        src={url}
                      />
                    </CarouselItem>
                  )
                })}
              </Carousel>
            </Box>
          )}

          <Box sx={contentWrapper}>
            <CardHeader sx={cardHeader} title={t(event.name)} />

            {event.showInfo && (
              <List sx={infoContainer}>
                {!event.showAlways && (
                  <InfoItem
                    sx={infoListItem}
                    title={t("Duration")}
                    primary={event.durationDate}
                    secondary={event.openingText}
                  />
                )}
                {event.hasLocation ? (
                  <>
                    <Divider sx={infoDivider} />
                    <InfoItem
                      sx={infoListItem}
                      primary={
                        <LocationInfo
                          feature={event.feature}
                          component="span"
                        />
                      }
                      title={t("Location")}
                    />
                  </>
                ) : null}
              </List>
            )}

            {event.descriptionText && (
              <CardContent sx={cardContent}>
                <Typography sx={cardContentPrimaryText} gutterBottom>
                  {t("Event Detail")}
                </Typography>
                <Typography
                  sx={cardContentSecondaryText}
                  dangerouslySetInnerHTML={{
                    __html: event.descriptionText,
                  }}
                />
              </CardContent>
            )}

            {event.websiteLink && (
              <Box sx={websiteContainer}>
                <Typography sx={websiteTitle}>{t("Website")}</Typography>
                <Link to={event.websiteLink}>
                  <Typography sx={websiteLinkText}>
                    {event.websiteLink}
                  </Typography>
                </Link>
              </Box>
            )}
            {event.hasLocation && (
              <Box sx={viewLocationContainer}>
                <Button
                  sx={viewLocationButton}
                  variant="contained"
                  startIcon={locationIcon}
                  onClick={navigateToLocation}
                >
                  {t("View Location")}
                </Button>
              </Box>
            )}
          </Box>
        </Card>
      </Layout>
    </>
  )
}
